/**
 * Unit utilities for distance conversions and locale detection
 */

// Countries that use miles (US, UK, Myanmar, Liberia)
const MILES_COUNTRIES = ['US', 'GB', 'MM', 'LR'];

/**
 * Detect the user's locale/country from browser settings
 * @returns {string} Two-letter country code (e.g., 'US', 'FR')
 */
export function detectUserLocale() {
  if (typeof navigator === 'undefined') return 'US';

  // Try to get locale from navigator.language or navigator.languages
  const language = navigator.language || navigator.languages?.[0] || 'en-US';

  // Extract country code from locale string (e.g., 'en-US' -> 'US')
  const parts = language.split('-');
  const countryCode = parts[1] || parts[0].toUpperCase();

  return countryCode;
}

/**
 * Get the default unit based on user's locale/country
 * @param {string} countryCode - Two-letter country code
 * @returns {'miles'|'km'} The default unit for that country
 */
export function getDefaultUnit(countryCode) {
  const normalized = countryCode?.toUpperCase();

  if (MILES_COUNTRIES.includes(normalized)) {
    return 'miles';
  }

  return 'km';
}

/**
 * Convert distance from one unit to another
 * @param {number} value - Distance value to convert
 * @param {'miles'|'km'} fromUnit - Source unit
 * @param {'miles'|'km'} toUnit - Target unit
 * @returns {number} Converted distance value
 */
export function convertDistance(value, fromUnit, toUnit) {
  if (fromUnit === toUnit) return value;

  // Miles to kilometers: multiply by 1.60934
  if (fromUnit === 'miles' && toUnit === 'km') {
    return value * 1.60934;
  }

  // Kilometers to miles: divide by 1.60934
  if (fromUnit === 'km' && toUnit === 'miles') {
    return value / 1.60934;
  }

  return value;
}

/**
 * Format distance with appropriate unit suffix
 * @param {number} value - Distance value
 * @param {'miles'|'km'} unit - Unit to use
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted distance string (e.g., '2.5 mi', '4.0 km')
 */
export function formatDistance(value, unit, decimals = 1) {
  if (value == null || isNaN(value)) return '-- --';

  const rounded = Number(value.toFixed(decimals));
  const suffix = unit === 'miles' ? 'mi' : 'km';

  return `${rounded} ${suffix}`;
}

/**
 * Get the Earth's radius in the specified unit
 * Used for haversine distance calculations
 * @param {'miles'|'km'} unit - Unit to use
 * @returns {number} Earth's radius in the specified unit
 */
export function getEarthRadius(unit) {
  // Earth's radius in miles: 3958.7613
  // Earth's radius in kilometers: 6371
  return unit === 'miles' ? 3958.7613 : 6371;
}
